import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { supabase } from '@/api/supabaseClient';
import { signInWithGoogle } from '@/lib/googleAuth';
import { useAuth } from '@/lib/AuthContext';
import { useTenantBranding } from '@/lib/TenantBrandingContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import defaultLogoUrl from '@/assets/logo-horizontal.png';

const slugify = (s) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'carrier';

// Client-side soft lockout after repeated failed sign-ins. Tracked
// per-email in localStorage, not server-side -- this deters casual
// repeated typos/guessing without new backend infra, but a determined
// attacker could bypass it (clear storage, or hit Supabase's API
// directly). Real brute-force protection would need a server-side
// table/edge function; this is a UX guard, not a hardened lock.
const MAX_LOGIN_ATTEMPTS = 5;
const LOCKOUT_MS = 15 * 60 * 1000;
const attemptsKey = (email) => `prohaul.login_attempts.${email}`;
const readAttempts = (email) => {
  try {
    const raw = localStorage.getItem(attemptsKey(email));
    return raw ? JSON.parse(raw) : { count: 0, lockedUntil: null };
  } catch { return { count: 0, lockedUntil: null }; }
};
const writeAttempts = (email, rec) => {
  try { localStorage.setItem(attemptsKey(email), JSON.stringify(rec)); } catch { /* storage disabled */ }
};
const clearAttempts = (email) => {
  try { localStorage.removeItem(attemptsKey(email)); } catch { /* ignore */ }
};

// Module scope (NOT inside the component) — defining it inside would create a
// new component type every render and the inputs would lose focus per keystroke.
//
// Brand-aligned light theme per the Pro Haul Brand Style Guide:
//   bg = white (#FFFFFF, the preferred brand background)
//   text = Pro Haul Black (#231F20)
//   accent / primary action = Pro Haul Red (#E22129) -- applied inline on
//                              the Sign-in / Subscribe buttons.
const Shell = ({ title, subtitle, logoSrc = defaultLogoUrl, children }) => (
  <div className="min-h-screen bg-prohaul-white text-prohaul-black flex flex-col items-center justify-center px-4 py-10">
    <div className="w-full max-w-sm space-y-8 flex-1 flex flex-col justify-center">
      <div className="text-center">
        {/* Brand logo, bare on the white background (no container --
            brand guide explicitly forbids drop-shadows / outlines on
            the raw asset). w-96 (384px) anchors the form. logoSrc lets a
            tenant-subdomain visit (gftrucklines.prohaul.ai) show that
            carrier's own uploaded logo instead of the default wordmark. */}
        <img
          src={logoSrc}
          alt="Pro Haul"
          className="w-96 mx-auto mb-6 block"
        />
        <h1 className="text-2xl font-stencil font-bold text-prohaul-black tracking-wide">
          {title}
        </h1>
        {subtitle && <p className="text-prohaul-black/70 text-sm mt-1">{subtitle}</p>}
      </div>
      {children}
    </div>
    {/* Legal footer — public-discoverable for Stripe verification +
        prospective customers. Subtle gray separator above. */}
    <div className="w-full max-w-sm mt-10 pt-5 border-t border-prohaul-black/10">
      <footer className="text-xs text-prohaul-black/60 flex items-center justify-center gap-4">
        <a href="/terms"   className="hover:text-prohaul-red">Terms</a>
        <span className="opacity-40">·</span>
        <a href="/privacy" className="hover:text-prohaul-red">Privacy</a>
        <span className="opacity-40">·</span>
        <a href="mailto:support@prohaul.ai" className="hover:text-prohaul-red">Contact</a>
      </footer>
    </div>
  </div>
);

export default function CarrierAuth() {
  const { isAuthenticated, hasCarrier, refreshUser, logout, user } = useAuth();
  const { status: tenantStatus, carrierName: tenantCarrierName, carrierLogoUrl: tenantLogoUrl } = useTenantBranding();
  // Any subdomain-shaped host (loading/found/error -- 'not_found' is handled
  // by its own early return below) implies "I already know which company
  // this is" -- self-serve signup / create-a-new-carrier never make sense
  // there, regardless of whether the branding lookup itself succeeded.
  const isTenantScoped = tenantStatus !== 'none';
  const [mode, setMode] = useState('login');     // 'login' | 'signup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [verifySent, setVerifySent] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [lockoutUntil, setLockoutUntil] = useState(null);

  useEffect(() => {
    if (mode !== 'login' || !email.trim()) { setLockoutUntil(null); return; }
    const rec = readAttempts(email.trim().toLowerCase());
    setLockoutUntil(rec.lockedUntil && rec.lockedUntil > Date.now() ? rec.lockedUntil : null);
  }, [email, mode]);

  // Un-sticks "Continue with Google" if the user backs out of the system
  // browser tab without completing sign-in (closes it, presses back,
  // cancels on Google's consent screen) -- that path never fires the
  // appUrlOpen redirect googleAuth.js listens for, so nothing else would
  // ever clear the loading state the button was left in. Native only;
  // the web flow is a full-page redirect, so this component unmounts
  // entirely rather than sitting around in a stuck state.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const listener = CapacitorApp.addListener('resume', () => {
      setGoogleLoading(false);
    });
    return () => { listener.then(l => l.remove()); };
  }, []);

  // ---- unknown/typo'd tenant subdomain -> nothing to sign into here ----
  if (tenantStatus === 'not_found') {
    return (
      <Shell title="Carrier not found" subtitle="We couldn't find a company at this address">
        <p className="text-sm text-prohaul-black/70 text-center">
          Double-check the link your company gave you, or head to{' '}
          <a href="https://prohaul.ai" className="text-prohaul-red underline">prohaul.ai</a>{' '}
          to sign in.
        </p>
      </Shell>
    );
  }

  // ---- authenticated but no carrier yet ----
  if (isAuthenticated && !hasCarrier) {
    // On a specific carrier's subdomain, being authenticated with no
    // carrier means this account hasn't been invited to THIS carrier --
    // letting them spin up a brand-new carrier from here would be wrong.
    if (isTenantScoped) {
      return (
        <Shell
          title="No access yet"
          subtitle={tenantCarrierName ? `Sign in to ${tenantCarrierName}` : 'Access your fleet portal'}
        >
          <p className="text-sm text-prohaul-black/70 text-center">
            Your account isn't linked to this carrier yet. Check your email for an invite link, or contact your company's administrator.
          </p>
          <button
            type="button"
            onClick={logout}
            className="w-full text-center text-xs text-prohaul-black/60 hover:text-prohaul-red underline"
          >
            Sign out
          </button>
        </Shell>
      );
    }
    const createCarrier = async (e) => {
      e.preventDefault();
      if (!companyName.trim()) return;
      setLoading(true);
      try {
        const slug = `${slugify(companyName)}-${Math.random().toString(36).slice(2, 6)}`;
        const { error } = await supabase.rpc('create_carrier_with_owner', {
          p_carrier_name: companyName.trim(),
          p_slug: slug,
        });
        if (error) {
          toast.error(error.message || 'Could not create carrier. Try again.');
          return;
        }
        await refreshUser();   // re-resolves carrier -> routing enters the app
      } catch (err) {
        toast.error(err?.message || 'Could not create carrier. Try again.');
      } finally {
        setLoading(false);
      }
    };
    return (
      <Shell title="Create your carrier" subtitle={`Signed in as ${user?.email}`}>
        <form onSubmit={createCarrier} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="company" className="text-prohaul-black">Carrier / Company name</Label>
            <Input
              id="company"
              value={companyName}
              onChange={e => setCompanyName(e.target.value)}
              placeholder="Garnica Family Trucking"
              required
              autoFocus
              className="bg-white border border-prohaul-black/20 text-prohaul-black placeholder:text-prohaul-black/40"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-prohaul-red hover:bg-prohaul-red/90 text-white font-semibold"
          >
            {loading ? 'Creating…' : 'Create carrier & continue'}
          </Button>
          <button
            type="button"
            onClick={logout}
            className="w-full text-center text-xs text-prohaul-black/60 hover:text-prohaul-red underline"
          >
            Sign out
          </button>
        </form>
      </Shell>
    );
  }

  // ---- not authenticated -> signup / login ----
  if (verifySent) {
    return (
      <Shell title="Check your email" subtitle="We sent a verification link">
        <p className="text-sm text-prohaul-black/70 text-center">
          Click the link in the email we sent to <strong className="text-prohaul-black">{email}</strong>, then come back and sign in.
        </p>
        <Button
          variant="outline"
          onClick={() => { setVerifySent(false); setMode('login'); }}
          className="w-full bg-white border border-prohaul-black/20 text-prohaul-black hover:bg-prohaul-lightgray"
        >
          Back to sign in
        </Button>
      </Shell>
    );
  }

  if (resetSent) {
    return (
      <Shell title="Check your email" subtitle="We sent a password reset link">
        <p className="text-sm text-prohaul-black/70 text-center">
          Click the link in the email we sent to <strong className="text-prohaul-black">{email}</strong> to set a new password.
        </p>
        <Button
          variant="outline"
          onClick={() => setResetSent(false)}
          className="w-full bg-white border border-prohaul-black/20 text-prohaul-black hover:bg-prohaul-lightgray"
        >
          Back to sign in
        </Button>
      </Shell>
    );
  }

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      await signInWithGoogle();
      // Web: the browser is about to full-page-navigate to Google, so
      // there's nothing further to do here. Native: control returns
      // immediately once the system browser tab opens -- the actual
      // sign-in completes later via App.jsx's appUrlOpen listener, which
      // is why this doesn't clear googleLoading itself; the whole page
      // either navigates away (web) or the user backgrounds this screen
      // to finish in the browser tab (native).
    } catch (err) {
      toast.error(err?.message || 'Could not start Google sign-in.');
      setGoogleLoading(false);
    }
  };

  const forgotPassword = async () => {
    if (!email.trim()) { toast.error('Enter your email above first.'); return; }
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) { toast.error(error.message); return; }
      // Supabase sends the reset email regardless of whether the address
      // is registered (same anti-enumeration behavior as signUp) -- so
      // this always "succeeds" here; a nonexistent email just gets no
      // actual email, same as elsewhere in this flow.
      setResetSent(true);
    } catch (err) {
      toast.error(err?.message || 'Could not send reset email.');
    } finally {
      setLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!supabase) {
      toast.error('Auth is not configured (missing Supabase env vars).');
      return;
    }
    const emailKey = email.trim().toLowerCase();
    if (mode === 'login') {
      const rec = readAttempts(emailKey);
      if (rec.lockedUntil && rec.lockedUntil > Date.now()) {
        const mins = Math.ceil((rec.lockedUntil - Date.now()) / 60000);
        toast.error(`Too many failed attempts. Try again in ${mins} minute${mins === 1 ? '' : 's'}.`);
        return;
      }
    }
    setLoading(true);
    try {
      if (mode === 'signup') {
        const { data, error } = await supabase.auth.signUp({
          email, password,
          options: { emailRedirectTo: window.location.origin },
        });
        if (error) { toast.error(error.message); return; }
        // Anti-enumeration: signUp for an email that's already registered
        // returns a 200 with no error and a "fake" user (empty identities,
        // no session) instead of a real error -- indistinguishable from a
        // genuine new signup unless this is checked, so the "check your
        // email" screen below became a dead end with nothing ever sent.
        if (data?.user && data.user.identities?.length === 0) {
          setMode('login');
          toast.error('An account with this email already exists. Sign in instead.');
          return;
        }
        setVerifySent(true);   // C1: mandatory email verification
        return;
      }
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        const rec = readAttempts(emailKey);
        const count = rec.count + 1;
        if (count >= MAX_LOGIN_ATTEMPTS) {
          const lockedUntil = Date.now() + LOCKOUT_MS;
          writeAttempts(emailKey, { count, lockedUntil });
          setLockoutUntil(lockedUntil);
          toast.error('Too many failed attempts. Sign-in is locked for 15 minutes.');
        } else {
          writeAttempts(emailKey, { count, lockedUntil: null });
          const left = MAX_LOGIN_ATTEMPTS - count;
          toast.error(`${error.message} (${left} attempt${left === 1 ? '' : 's'} left)`);
        }
        return;
      }
      clearAttempts(emailKey);
      // AuthContext.onAuthStateChange picks up the session and routes onward
    } catch (err) {
      toast.error(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // A subdomain visit only ever offers sign-in -- the toggle to signup
  // mode is hidden below, so `mode` can never actually become 'signup'
  // while isTenantScoped is true, but the title/subtitle/button copy is
  // still made explicit here for clarity rather than relying on that.
  const effectiveMode = isTenantScoped ? 'login' : mode;

  return (
    <Shell
      logoSrc={tenantStatus === 'found' && tenantLogoUrl ? tenantLogoUrl : defaultLogoUrl}
      title={effectiveMode === 'signup' ? 'Start your carrier' : 'Sign in'}
      subtitle={
        isTenantScoped
          ? (tenantCarrierName ? `Sign in to ${tenantCarrierName}` : 'Access your fleet portal')
          : (effectiveMode === 'signup' ? '14-day free trial · no card' : 'Access your fleet portal')
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-prohaul-black">Email</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            autoComplete="email"
            placeholder="you@company.com"
            className="bg-white border border-prohaul-black/20 text-prohaul-black placeholder:text-prohaul-black/40"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password" className="text-prohaul-black">Password</Label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
            placeholder="••••••••"
            minLength={8}
            className="bg-white border border-prohaul-black/20 text-prohaul-black placeholder:text-prohaul-black/40"
          />
          {effectiveMode === 'login' && (
            <button
              type="button"
              onClick={forgotPassword}
              disabled={loading}
              className="text-xs text-prohaul-black/60 hover:text-prohaul-red underline"
            >
              Forgot password?
            </button>
          )}
        </div>
        <Button
          type="submit"
          disabled={loading || (effectiveMode === 'login' && !!lockoutUntil)}
          className="w-full bg-prohaul-red hover:bg-prohaul-red/90 text-white font-semibold"
        >
          {loading ? 'Please wait…'
            : (effectiveMode === 'login' && lockoutUntil) ? 'Too many attempts -- try later'
            : effectiveMode === 'signup' ? 'Create account' : 'Sign in'}
        </Button>
      </form>
      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-prohaul-black/10" />
        <span className="text-xs text-prohaul-black/50">or</span>
        <div className="flex-1 h-px bg-prohaul-black/10" />
      </div>
      <Button
        type="button"
        variant="outline"
        onClick={handleGoogleSignIn}
        disabled={googleLoading}
        className="w-full bg-white border border-prohaul-black/20 text-prohaul-black hover:bg-prohaul-lightgray font-medium"
      >
        <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" aria-hidden="true">
          <path fill="#4285F4" d="M23.52 12.27c0-.85-.08-1.67-.22-2.45H12v4.64h6.47c-.28 1.5-1.13 2.78-2.4 3.63v3.02h3.88c2.27-2.09 3.57-5.17 3.57-8.84z" />
          <path fill="#34A853" d="M12 24c3.24 0 5.96-1.08 7.95-2.9l-3.88-3.02c-1.08.72-2.45 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.27v3.11C3.25 21.3 7.31 24 12 24z" />
          <path fill="#FBBC05" d="M5.27 14.27A7.2 7.2 0 0 1 4.9 12c0-.79.14-1.56.37-2.27V6.62H1.27A11.98 11.98 0 0 0 0 12c0 1.93.46 3.76 1.27 5.38l4-3.11z" />
          <path fill="#EA4335" d="M12 4.77c1.76 0 3.35.61 4.6 1.8l3.44-3.44C17.95 1.19 15.24 0 12 0 7.31 0 3.25 2.7 1.27 6.62l4 3.11C6.22 6.88 8.87 4.77 12 4.77z" />
        </svg>
        {googleLoading ? 'Please wait…' : 'Continue with Google'}
      </Button>
      {!isTenantScoped && (
        <p className="text-center text-xs text-prohaul-black/60">
          {mode === 'signup' ? 'Already have an account?' : 'New carrier?'}{' '}
          <button
            onClick={() => setMode(mode === 'signup' ? 'login' : 'signup')}
            className="underline text-prohaul-red hover:text-prohaul-red/80"
          >
            {mode === 'signup' ? 'Sign in' : 'Start a free trial'}
          </button>
        </p>
      )}
    </Shell>
  );
}
